import { Image } from 'react-native';
import { createReducer, createAction } from '@reduxjs/toolkit';
import i18n, { Scope } from 'i18n-js';
import moment from 'moment';
import { build } from 'search-params';

import type { AnyAction } from '@reduxjs/toolkit';

import { backendCall, createApiCall, errorHandl, unlogin } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import type { AppDispatch } from '@reducers/utils/types';
import type { SearchImagesRequestDto } from '@api/generated/models';
import type { RootState, AppThunk } from '@store/types';
import { trackUserLoggedIn } from '@services/tracking';
import {
  removeSecureValue,
  setSecureValue,
  savePasswordToiCloud,
  Alert,
  getSecureValue,
  IDENTITY_SERVER_DATA,
  isWeb,
  resetTo,
  ALL_MACHINES,
  FEATURE_FLAGS,
  ENVIRONMENTS,
  languages,
  languagePrefixes,
} from '../../helpers';

import { generateImages } from '@components/imageHandling/common';

import { initRole } from '@components/frame';

import { getDeviceDetails } from '@reducers/device';
import { generateImageURL } from '@reducers/blobStorage';
import { initSignalR, closeConnections } from '@reducers/signalR';
import { getTimeSetup } from '@reducers/time';
import { getMachines } from '@reducers/machines';
import { getConnectorInfo } from '@reducers/connector';
import { connectorProductionConfigurationDetails, setIsWithERP } from '@reducers/membership';
import { parseAndAddUrlsToImagesDocuments } from '@reducers/v2/imageHandlingV2/thunks';
import { setPersistUserEmail } from '@store/persistUserScope';

import { generateTokenInfo, getBlob } from './utils';
import { exitRestrictedRouteIfTenantOK } from '@reducers/helpers/tenantStatus';

import type {
  UserState,
  UserStateInfo,
  UserLoginParams,
  LanguageInfo,
  User,
  UserProfileDetailsResponse,
  LoginResponse,
  VerifyEmailRequestDto,
  PasswordResetRequestDto,
  VerifyPasswordResetRequestDto,
  ResetPasswordRequestDto,
  ConfirmUserInviteRequestDto,
  ChangePasswordRequestDto,
  SubmitNewProspectRequestDto,
} from './types';

// Re-export types for consumers
export type {
  UserState,
  UserLoginParams,
  LanguageInfo,
  User,
  UserProfileDetailsResponse,
  VerifyEmailRequestDto,
  PasswordResetRequestDto,
  VerifyPasswordResetRequestDto,
  ResetPasswordRequestDto,
  ConfirmUserInviteRequestDto,
  ChangePasswordRequestDto,
} from './types';

// Re-export utils for consumers
export { generateTokenInfo, getBlob } from './utils';

// === Initial State ===

const initState: UserState = {
  user: {},
  loaded: false,
  userInfo: {},
  featureFlags: {},
  featureFlagsEnv: {},
  waitForNewAccount: false,
};

// === Selectors ===

export const selectUserInfo = (state: RootState) => state.user.userInfo;
export const selectFeatureFlags = (state: RootState) => state.user.featureFlags;
export const selectCurrentUserId = (state: RootState): number | undefined => state.user.userInfo?.userProfile?.userId;

// === Actions ===

export const setUser = createAction<Partial<User>>('user/user');
export const setUserInfo = createAction<UserStateInfo>('user/setUserInfo');
export const updateUserInfo = createAction<Record<string, unknown>>('user/updateUserInfo');
export const updateCompanyProfile = createAction<Record<string, unknown>>('user/updateCompanyProfile');
export const setFeatureFlags = createAction<Record<string, boolean>>('user/setFeatureFlags');
export const setFeatureFlagsEnv = createAction<Record<string, Record<string, Record<string, boolean>>>>('user/setFeatureFlagsEnv');
export const updateUserPicture = createAction<Record<string, unknown> | null>('user/updateUserPicture');
export const updateWaitForNewAccount = createAction<boolean>('user/setFeatuupdateWaitForNewAccountreFlags');

export const clear = createAction('clear');

// === Thunks ===

export const userLogin =
  ({ email, pass, callback, scope = 'openid offline_access https://smarfac.onmicrosoft.com/api/access_as_user' }: UserLoginParams) =>
  async (dispatch: AppDispatch) => {
    if (isWeb) {
      return dispatch(unlogin());
    }

    const currentToken = getSecureValue('token');
    removeSecureValue('token');

    const ISData = {
      grant_type: 'password',
      username: email,
      password: pass,
      client_id: IDENTITY_SERVER_DATA.client_id,
      scope,
    };

    var formBody = build(ISData);

    const onSuccess = async (data: LoginResponse) => {
      setSecureValue('token', data.access_token);
      setSecureValue('refreshToken', data.refresh_token ?? null);
      setSecureValue('expiresIn', moment().add(data.expires_in, 's').format('YYYY-MM-DD HH:mm:ss'));
      await generateTokenInfo(data.access_token);
      await initRole();
      const role = getSecureValue('role');
      if (callback) {
        callback(data.access_token);
        return data;
      } else {
        await savePasswordToiCloud('login.smarfac.com', email, pass);
        resetTo('Authorized');
      }
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.user.login, {
          ignoreDefaultErrorHandler: true,
          succesEventCallback: true,
          anonym: true,
          params: formBody,
          onSuccess: (d) => onSuccess(d),
          onError: async (error) => {
            console.log(error, 'dev IS login error');
            if (currentToken) {
              setSecureValue('token', currentToken);
            }
            return error;
          },
        })
      )
    );
  };

export const checkStatus = (info: VerifyEmailRequestDto) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.user.checkStatus, {
        params: info,
        anonym: true,
      })
    )
  );
};

export const forgotPassword = (params: PasswordResetRequestDto, callback?: () => void) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.user.forgotPassword, {
        anonym: true,
        params,
        onSuccess: async (data) => {
          if (data.isSuccess && callback) {
            callback();
          } else {
            Alert({ title: i18n.t((data.errorMessage ? data.errorMessage : data) as Scope), type: 'danger' });
          }
        },
        onError: (error) => {
          Alert({ title: i18n.t((error?.errorMessage ? error.errorMessage : error) as Scope), type: 'danger' });
        },
      })
    )
  );
};

export const forgotPasswordCheckCode = (params: VerifyPasswordResetRequestDto, callback?: () => void) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.user.forgotPasswordCheckCode, {
        anonym: true,
        params,
        onSuccess: async (data) => {
          if (data.isSuccess && callback) {
            callback();
          } else {
            Alert({ title: i18n.t((data.errorMessage ? data.errorMessage : data) as Scope), type: 'danger' });
          }
        },
        onError: (error) => {
          Alert({ title: i18n.t((error?.errorMessage ? error.errorMessage : error) as Scope), type: 'danger' });
        },
      })
    )
  );
};

export const resetPassword = (params: ResetPasswordRequestDto, callback?: () => void) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.user.resetPassword, {
        anonym: true,
        params,
        onSuccess: async (data) => {
          if (data.isSuccess && callback) {
            callback();
          } else {
            Alert({ title: i18n.t((data.errorMessage ? data.errorMessage : data) as Scope), type: 'danger' });
          }
        },
        onError: (error) => {
          Alert({ title: i18n.t((error?.errorMessage ? error.errorMessage : error) as Scope), type: 'danger' });
        },
      })
    )
  );
};

export const confirmInviteVerify = (params: ConfirmUserInviteRequestDto, callback?: () => void) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.user.confirmInviteVerify, {
        anonym: true,
        params,
      })
    )
  );
};

export const createAccount =
  (data: Omit<SubmitNewProspectRequestDto, 'deviceInformation'>, callback: () => void) => async (dispatch: AppDispatch) => {
    const deviceInformation = await getDeviceDetails();

    await dispatch(
      backendCall(
        createApiCall((api) => api.ticket.prospect, {
          anonym: true,
          params: {
            deviceInformation: {
              ...deviceInformation,
              type: deviceInformation.deviceType,
            },
            ...data,
          },
          onSuccess: async (data) => {
            if (!data?.isSuccess) {
              errorHandl(data as { responseException?: { exceptionMessage?: string | undefined } | undefined });
              return;
            }
            callback();
          },
          onError: (error) => {
            console.log(error, 'error');
            if (error?.isError) {
              errorHandl(error);
              return;
            }
          },
        })
      )
    );
  };

export const changePassword = (data: ChangePasswordRequestDto, callback: () => void) => async (dispatch: AppDispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.user.changePassword, {
        params: data,
        onSuccess: async (data) => {
          if (data.errorMessage) {
            Alert({ title: data.errorMessage });
          } else {
            Alert({ title: i18n.t('Success'), type: 'success' });
            callback();
          }
        },
        onError: (error) => {
          console.log(error, 'error');
          Alert({ title: error?.title as string });
        },
      })
    )
  );
};

export const receivedNewAsyncAccount = (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
  const {
    user: { waitForNewAccount },
  } = getState();

  if (waitForNewAccount) {
    await dispatch(selectNewAccount(false, true));
    return true;
  }
  return false;
};

export const checkLogoutEvent =
  (e: { emailAddress: string }): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      user: { userInfo },
    } = getState();

    let user: Record<string, unknown> = userInfo;
    if (!user?.emailAddress) {
      user = (await dispatch(getAuthInfo())) as Record<string, unknown>;
    }

    if (e.emailAddress === user.emailAddress) {
      dispatch(unlogin());
    }
  };

// Shared reinitialize sequence — used after account switching on both web (MSAL) and native
export const reinitializeAppState = (): AppThunk<Promise<void>> => async (dispatch) => {
  // Phase 1: Teardown — close connections before clearing state they reference
  await dispatch(closeConnections());
  await dispatch(clear());

  // Flip persist scope to the new account AFTER clear() — if it flipped earlier
  // (inside setMsalSession), intermediate persist writes during closeConnections
  // land the previous account's state in the new account's persist:root:<email>.
  if (isWeb) {
    const email = getSecureValue('email');
    if (email) setPersistUserEmail(email);
  }

  // Phase 2: Identity — both are independent API calls that set user context
  // getAuthInfo calls initRole(data.role) internally via onSuccess
  const [, tenantOK] = await Promise.all([dispatch(getAuthInfo()), generateTokenInfo(false)]);
  await initRole();

  // Phase 3: Data — independent API calls, all use Bearer token
  await Promise.all([
    dispatch(getAllFeatureFlags()),
    dispatch(getMachines(false, isWeb ? undefined : ALL_MACHINES)),
    dispatch(getTimeSetup()),
    dispatch(getConnectorInfo()),
    dispatch(connectorProductionConfigurationDetails()),
  ]);

  // Phase 4: Reconnect — after all state is loaded
  await dispatch(initSignalR());

  // Phase 5: If the previous tenant left us on a restricted route (e.g. /onboarding-wizard,
  // /on-hold) and the new tenant is OK, reset to the default authorized landing.
  // No-op when the new tenant is restricted — `handleTenantReasonResponse` routed to
  // the correct restricted screen for awaited tenant-reason responses during reinit.
  exitRestrictedRouteIfTenantOK(tenantOK);
};

export const selectNewAccount =
  (data: Record<string, unknown> | false, addToStorage = false) =>
  async (dispatch: AppDispatch) => {
    if (data) {
      const acc = getSecureValue('accounts');
      if (acc) {
        const accounts = JSON.parse(acc);
        const account = accounts.find((a) => a.userInfo?.emailAddress === data.userInfo?.emailAddress);

        setSecureValue('token', account.token);
        setSecureValue('refreshToken', account.refreshToken);
        setSecureValue('expiresIn', account.expiresIn);
        if (account.deviceCode) {
          setSecureValue('deviceCode', account.deviceCode);
        }
        setSecureValue('env', account.env);
        setSecureValue('role', account.role || '');
        setSecureValue('email', account.email);
      }
    }

    if (addToStorage) {
      await dispatch(closeConnections());
      await dispatch(clear());
      const userInfo = await dispatch(getAuthInfo());
      const acc = getSecureValue('accounts');
      if (acc) {
        const accs = JSON.parse(acc);

        const currentAccount = [
          ...accs,
          {
            userInfo,
            token: getSecureValue('token'),
            expiresIn: getSecureValue('expiresIn'),
            refreshToken: getSecureValue('refreshToken'),
            deviceCode: getSecureValue('deviceCode'),
            env: getSecureValue('env'),
            role: getSecureValue('role'),
            email: getSecureValue('email'),
          },
        ];
        setSecureValue('accounts', JSON.stringify(currentAccount));
      }
      await initRole();
      await dispatch(getAllFeatureFlags());
      await generateTokenInfo(false);
      // Native: preselect All Machines (see reinitializeAppState) so adding/switching to
      // the new account doesn't land on an empty "No Machine Selected" state.
      await dispatch(getMachines(false, isWeb ? undefined : ALL_MACHINES));
      await dispatch(getTimeSetup());
      await dispatch(getConnectorInfo());
      await dispatch(connectorProductionConfigurationDetails());
      await dispatch(initSignalR());
    } else {
      await dispatch(reinitializeAppState());
    }
  };

export const getUserPictureInfo =
  (id: string): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const data: SearchImagesRequestDto = {
      filters: {
        UserAccount: {
          value: id,
          valueType: 'string',
        },
      },
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.imageHandling.getUserProfileImages, {
          params: data,
        })
      )
    );
  };

export const resolveLanguageWithPrefix = (
  preferredLanguage: string | undefined,
  language: Pick<LanguageInfo, 'lg' | 'updateLanguage'>,
  prefix?: string,
): void => {
  if (!preferredLanguage) return;

  const isCurrentLanguageWithPrefix = languagePrefixes.some((p) => preferredLanguage?.split('-')[1] === p);
  if (isCurrentLanguageWithPrefix || !prefix) {
    if (language.lg != preferredLanguage) {
      language.updateLanguage?.(preferredLanguage);
    }
    return;
  }

  const lgWithPrefix = `${preferredLanguage}-${prefix}`;
  const languageWithPrefixExist = languages.some((lng) => lng.value === lgWithPrefix);
  if (languageWithPrefixExist) {
    language.updateLanguage?.(lgWithPrefix);
  } else if (language.lg != preferredLanguage) {
    language.updateLanguage?.(preferredLanguage);
  }
};

export const getAuthInfo =
  (noUpdate = false, language?: LanguageInfo): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const previousUserInfo = getState().user.userInfo;

    return await dispatch(
      backendCall(
        createApiCall((api) => api.user.userProfileDetails, {
          onSuccess: async (data) => {
            if (noUpdate) {
              return data;
            }

            initRole(data.role);

            if (language) {
              resolveLanguageWithPrefix(data?.userProfile?.preferedLanguage, language, language.prefix);
            }

            dispatch(setIsWithERP(!!data?.companyProfile?.hasERP));

            const userInfo: UserStateInfo = { ...data, userPicture: null, userPictureLoaded: true };

            if (data?.userProfile?.userId) {
              try {
                const info = await dispatch(
                  backendCall(
                    createApiCall((api) => api.imageHandling.searchDocuments, {
                      params: {
                        search: '*',
                        filters: {
                          UserAccount: {
                            value: data?.userProfile?.userId,
                            valueType: 'string',
                          },
                        },
                      },
                    })
                  )
                );

                if (info?.searchResult?.length) {
                  const userPicture = info.searchResult[info?.searchResult?.length - 1];
                  const imageData = await dispatch(
                    parseAndAddUrlsToImagesDocuments({
                      imagesDocuments: [
                        {
                          ...userPicture,
                          cropSettings: userPicture.imageCropSettings,
                          pageDetails: userPicture.imageDetails,
                        },
                      ],
                    })
                  );
                  let image;

                  if (parseAndAddUrlsToImagesDocuments.fulfilled.match(imageData)) {
                    image = imageData?.payload?.[0];
                  } else {
                    //Fallback to old solution
                    image = (await generateImages({ images: [userPicture] }, (d: Record<string, unknown>) => dispatch(generateImageURL(d))))[0];
                  }

                  image.base64 = await getBlob(image.url, image.id);

                  let imageInfo = {};
                  try {
                    // Image.getSize uses callbacks, not Promises — calling without callbacks caused "success is not a function" on web
                    imageInfo = await new Promise<{ width: number; height: number }>((resolve, reject) => {
                      Image.getSize(image.url, (width, height) => resolve({ width, height }), reject);
                    });
                  } catch (e) {}
                  userInfo.userPicture = { ...image, ...imageInfo };
                }
              } catch (e) {
                userInfo.userPicture = null;
                userInfo.userPictureLoaded = true;
              }
            }

            dispatch(setUserInfo(userInfo));

            // send login log event when user info is first loaded (after successful login)
            if (!previousUserInfo?.emailAddress && data?.emailAddress) {
              trackUserLoggedIn(data);
            }

            return data;
          },
          onError: async (error) => {
            console.error('[getAuthInfo] userProfileDetails failed', error);
            dispatch(sendLog({ title: LOGGER_TITLES.AUTH_INFO_PROFILE_FAILED, data: { error: String(error) }, status: 'error' }) as unknown as AnyAction);
            if (isWeb) {
              return;
            }
            const email = getSecureValue('email');
            if (email) {
              const data = {
                companyProfile: { companyName: ' ' },
                emailAddress: email,
                invitedOn: '',
                registeredOn: null,
                role: null,
                status: '',
                userProfile: { firstName: ' ', lastName: ' ' },
              };
              if (noUpdate) {
                return data;
              }
              dispatch(setUserInfo(data));
              return data;
            }
          },
        })
      )
    );
  };

export const getAllFeatureFlags = () => async (dispatch: AppDispatch) => {
  const en = getSecureValue('env');
  const featureEnv = getSecureValue('featureEnv');
  const env = featureEnv || en;
  try {
    const response = await fetch(
      'https://stgsharedwe.table.core.windows.net/Feature?sv=2019-02-02&se=2122-04-12T18%3A53%3A00Z&sp=r&sig=Ht3MuLYnyrwLdOtfoDXHbK36sGS8Vjti3adrX9B%2BBAI%3D&tn=Feature',
      {
        method: 'GET',
        headers: {
          Accept: 'application/json;odata=nometadata',
        },
        ignoreInterceptor: 'true',
      } as RequestInit
    );
    const json = await response.json();
    const parseResult = (result: Record<string, unknown>) => {
      const data: Record<string, boolean> = {};
      const dataEnv: Record<string, Record<string, Record<string, boolean>>> = {
        [ENVIRONMENTS.DEV]: {},
        [ENVIRONMENTS.QA]: {},
        [ENVIRONMENTS.STG]: {},
        [ENVIRONMENTS.PROD]: {},
      };
      const entry = result?.value as Record<string, unknown>[];

      if (!entry) {
        return;
      }

      entry.map((properties) => {
        const key = `${properties['PartitionKey']}-${properties['RowKey']}`;

        dataEnv[ENVIRONMENTS.DEV][properties['PartitionKey'] as string] = {};
        dataEnv[ENVIRONMENTS.QA][properties['PartitionKey'] as string] = {};
        dataEnv[ENVIRONMENTS.STG][properties['PartitionKey'] as string] = {};
        dataEnv[ENVIRONMENTS.PROD][properties['PartitionKey'] as string] = {};

        const envList: Record<string, boolean> = {
          [ENVIRONMENTS.DEV]: properties['Dev'] as boolean,
          [ENVIRONMENTS.QA]: properties['QA'] as boolean,
          [ENVIRONMENTS.STG]: properties['Stg'] as boolean,
          [ENVIRONMENTS.PROD]: properties['Prod'] as boolean,
        };

        data[key] = envList[env];
      });

      entry.map((properties) => {
        dataEnv[ENVIRONMENTS.DEV][properties['PartitionKey'] as string][properties['RowKey'] as string] = properties['Dev'] as boolean;
        dataEnv[ENVIRONMENTS.QA][properties['PartitionKey'] as string][properties['RowKey'] as string] = properties['QA'] as boolean;
        dataEnv[ENVIRONMENTS.STG][properties['PartitionKey'] as string][properties['RowKey'] as string] = properties['Stg'] as boolean;
        dataEnv[ENVIRONMENTS.PROD][properties['PartitionKey'] as string][properties['RowKey'] as string] = properties['Prod'] as boolean;
      });

      dispatch(setFeatureFlags(data));
      dispatch(setFeatureFlagsEnv(dataEnv));
    };

    parseResult(json);
  } catch (e) {
    console.log(e, 'flags error');
    const data: Record<string, boolean> = {};
    Object.keys(FEATURE_FLAGS[env]).forEach((key: string) => {
      const obj = FEATURE_FLAGS[env][key];
      Object.keys(obj).forEach((property: string) => {
        data[`${key}-${property}`] = obj[property];
      });
    });
    dispatch(setFeatureFlags(data));
    dispatch(setFeatureFlagsEnv(FEATURE_FLAGS));
  }
};

// === Reducer ===

export default createReducer<UserState>(initState, {
  [setUser.type]: (state: UserState, action: { payload: Partial<User> }) => ({
    ...state,
    user: action.payload,
  }),
  [setUserInfo.type]: (state: UserState, action: { payload: UserStateInfo }) => ({
    ...state,
    loaded: true,
    userInfo: action.payload,
  }),
  [updateUserInfo.type]: (state: UserState, action: { payload: Record<string, unknown> }) => ({
    ...state,
    userInfo: {
      ...state.userInfo,
      userProfile: {
        ...state.userInfo.userProfile,
        ...action.payload,
      },
    },
  }),
  [updateCompanyProfile.type]: (state: UserState, action: { payload: Record<string, unknown> }) => ({
    ...state,
    userInfo: {
      ...state.userInfo,
      companyProfile: {
        ...state.userInfo.companyProfile,
        ...action.payload,
      },
    },
  }),
  [updateUserPicture.type]: (state: UserState, action: { payload: Record<string, unknown> | null }) => ({
    ...state,
    userInfo: {
      ...state.userInfo,
      userPicture: action.payload,
    },
  }),
  [setFeatureFlags.type]: (state: UserState, action: { payload: Record<string, boolean> }) => ({
    ...state,
    loaded: true,
    featureFlags: action.payload,
  }),
  [setFeatureFlagsEnv.type]: (state: UserState, action: { payload: Record<string, Record<string, Record<string, boolean>>> }) => ({
    ...state,
    loaded: true,
    featureFlagsEnv: action.payload,
  }),
  [updateWaitForNewAccount.type]: (state: UserState, action: { payload: boolean }) => ({
    ...state,
    waitForNewAccount: action.payload,
  }),
  [clear.type]: () => {
    return initState;
  },
});
